/**
 * DBN Eligibility & Investment Assessment Module
 * Journey A: PFI Self-Assessment
 * Journey B: DBN Internal Officer Review
 * + Investment Readiness Radar + Workflow Visualization
 */
import React, { useState, useEffect } from "react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from "recharts";
import { dbnApi, PageHeader, SectionHeader, Badge, LoadingState, Btn, Modal, ScoreRing, fmtEur, eligColor, scoreColor } from "./dbnUtils";
import { ClipboardCheck, CheckCircle, XCircle, Clock, AlertTriangle, FileText, Send, ChevronRight, User, Building2, Plus } from "lucide-react";

// ── Criterion definitions ─────────────────────────────────────────────────────
const CRITERIA = [
  { key:"financial_score",    label:"Financial Eligibility",   weight:20, help:"Business viability, creditworthiness, debt service capacity, financial statements quality" },
  { key:"green_score",        label:"Green Eligibility",        weight:15, help:"Activity classification against DBN/EIB green taxonomy, environmental benefit, emissions reduction" },
  { key:"digital_score",      label:"Digital Eligibility",      weight:10, help:"Use of digital technology, platform component, digital access for underserved groups" },
  { key:"inclusion_score",    label:"Inclusion Assessment",     weight:10, help:"Women ownership, youth employment, rural area, conflict-affected state, disability inclusion" },
  { key:"climate_risk_score", label:"Climate Risk Screening",   weight:10, help:"Physical climate risk exposure, transition risk, vulnerability assessment, mitigation measures" },
  { key:"esg_score",          label:"ESG Assessment",           weight:10, help:"Environmental management, social safeguards, governance quality, ESMS in place" },
  { key:"safeguards_score",   label:"Safeguards",               weight:10, help:"IFC Performance Standards compliance, environmental permit, stakeholder engagement, ESIA" },
  { key:"additionality_score",label:"Additionality",            weight:10, help:"Financial additionality (tenor, rate, grace), project additionality, development additionality" },
  { key:"documentation_score",label:"Documentation Review",    weight: 5, help:"Completeness of submission, quality of business plan, financial projections, technical report" },
  { key:"readiness_score",    label:"Investment Readiness",     weight:10, help:"Management capability, PFI endorsement, regulatory status, project development stage" },
];

const STATUS_COLORS = {
  "Approved":           "#22c55e",
  "Sent to Committee":  "#3b82f6",
  "Rejected":           "#ef4444",
  "Under Review":       "#f59e0b",
  "Submitted":          "#60a5fa",
  "Information Requested": "#f97316",
  "TA Assigned":        "#a78bfa",
  "Escalated":          "#f97316",
};

// ── Journey A: PFI Self-Assessment Wizard ─────────────────────────────────────
function AssessmentWizard({ pfis, facilities, onSubmit }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    project_name:"", sponsor:"", pfi_id:"", facility_id:"", sector:"",
    state:"", finance_amount:"", project_type:"Green", submission_type:"PFI Self-Assessment",
  });
  const [scores, setScores] = useState(
    Object.fromEntries(CRITERIA.map(c => [c.key, 70]))
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await dbnApi.createAssessment({ ...form, ...scores, finance_amount: parseFloat(form.finance_amount) || 0 });
      setResult(r.data);
      setStep(2);
    } catch(e) {}
    setSubmitting(false);
  };

  if (step === 0) return (
    <div>
      <p style={{ fontSize:13, fontWeight:700, color:"#f0f9ff", marginBottom:14 }}>Step 1: Project Information</p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        {[
          { key:"project_name", label:"Project Name", type:"text", span:2 },
          { key:"sponsor", label:"Sponsor / Borrower", type:"text" },
          { key:"state", label:"State", type:"text" },
          { key:"finance_amount", label:"Finance Amount Requested (EUR)", type:"number" },
        ].map(f => (
          <div key={f.key} style={{ gridColumn: f.span===2?"span 2":"auto" }}>
            <label style={{ fontSize:10, color:"#64748b", display:"block", marginBottom:4, fontWeight:600 }}>{f.label}</label>
            <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({...p,[f.key]:e.target.value}))}
              style={{ width:"100%", background:"#060f1e", border:"1px solid #1e3a5f", borderRadius:8,
                padding:"8px 12px", fontSize:11, color:"#f0f9ff", boxSizing:"border-box" }} />
          </div>
        ))}
        {[
          { key:"pfi_id", label:"Participating Financial Institution", opts:pfis.map(p=>({val:p.id,lbl:p.name})) },
          { key:"facility_id", label:"Development Partner Facility", opts:facilities.map(f=>({val:f.id,lbl:`${f.partner} — ${f.name.slice(0,30)}`})) },
          { key:"project_type", label:"Project Type", opts:["Green","Digital","Green & Digital","General MSME"].map(v=>({val:v,lbl:v})) },
          { key:"sector", label:"Sector", opts:["Renewable Energy","Agriculture","Clean Transportation","Circular Economy","Green Manufacturing","Water and Sanitation","Healthcare","SME Recovery","Clean Energy"].map(v=>({val:v,lbl:v})) },
        ].map(f => (
          <div key={f.key}>
            <label style={{ fontSize:10, color:"#64748b", display:"block", marginBottom:4, fontWeight:600 }}>{f.label}</label>
            <select value={form[f.key]} onChange={e => setForm(p => ({...p,[f.key]:e.target.value}))}
              style={{ width:"100%", background:"#060f1e", border:"1px solid #1e3a5f", borderRadius:8,
                padding:"8px 12px", fontSize:11, color:"#f0f9ff" }}>
              <option value="">Select…</option>
              {f.opts.map(o => <option key={o.val} value={o.val}>{o.lbl}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end" }}>
        <Btn onClick={() => setStep(1)} color="#34d399">Continue to Scoring Criteria →</Btn>
      </div>
    </div>
  );

  if (step === 1) {
    const weighted = CRITERIA.reduce((acc, c) => acc + (scores[c.key] * c.weight / 100), 0);
    const overall = Math.round(weighted);
    const elig = overall >= 75 ? "Pass" : overall >= 60 ? "Conditional" : "Fail";
    const ec = eligColor(elig);

    return (
      <div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <p style={{ fontSize:13, fontWeight:700, color:"#f0f9ff" }}>Step 2: Eligibility Scoring Criteria</p>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:10, color:"#475569" }}>Live Score:</span>
            <span style={{ fontSize:16, fontWeight:900, color:ec }}>{overall}/100</span>
            <Badge label={elig} color={ec} />
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {CRITERIA.map(c => (
            <div key={c.key} style={{ background:"#060f1e", borderRadius:10, padding:"10px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                <div>
                  <span style={{ fontSize:11, fontWeight:600, color:"#94a3b8" }}>{c.label}</span>
                  <span style={{ fontSize:9, color:"#334155", marginLeft:8 }}>Weight: {c.weight}%</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:13, fontWeight:900, color:scoreColor(scores[c.key]) }}>{scores[c.key]}</span>
                  <Badge label={scores[c.key] >= 75 ? "Pass" : scores[c.key] >= 60 ? "Conditional" : "Fail"}
                    color={scoreColor(scores[c.key])} />
                </div>
              </div>
              <p style={{ fontSize:9, color:"#334155", marginBottom:6, lineHeight:1.4 }}>{c.help}</p>
              <input type="range" min={0} max={100} step={5} value={scores[c.key]}
                onChange={e => setScores(p => ({...p,[c.key]:+e.target.value}))}
                style={{ width:"100%", accentColor:scoreColor(scores[c.key]), cursor:"pointer" }} />
            </div>
          ))}
        </div>
        <div style={{ marginTop:16, display:"flex", justifyContent:"space-between" }}>
          <Btn outline color="#475569" onClick={() => setStep(0)}>← Back</Btn>
          <Btn onClick={handleSubmit} color="#34d399" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Assessment"}
          </Btn>
        </div>
      </div>
    );
  }

  if (step === 2 && result) {
    const ec = eligColor(result.eligibility_status);
    return (
      <div style={{ textAlign:"center", padding:"24px 0" }}>
        <div style={{ marginBottom:16 }}>
          <ScoreRing score={result.overall_score} size={80} />
        </div>
        <Badge label={result.eligibility_status} color={ec} />
        <p style={{ fontSize:18, fontWeight:900, color:ec, margin:"10px 0 6px" }}>
          Overall Score: {result.overall_score}/100
        </p>
        <p style={{ fontSize:11, color:"#94a3b8", lineHeight:1.6, maxWidth:480, margin:"0 auto 16px" }}>
          {result.recommendation}
        </p>
        <div style={{ background:"#060f1e", borderRadius:10, padding:"12px 16px", textAlign:"left", marginBottom:16 }}>
          <p style={{ fontSize:10, color:"#475569", marginBottom:6 }}>Reference: <strong style={{ color:"#34d399" }}>{result.id}</strong></p>
          <p style={{ fontSize:10, color:"#475569" }}>Status: <strong style={{ color:"#f0f9ff" }}>Submitted — Pending DBN Officer Review</strong></p>
        </div>
        <Btn onClick={() => { setStep(0); setResult(null); setForm({project_name:"",sponsor:"",pfi_id:"",facility_id:"",sector:"",state:"",finance_amount:"",project_type:"Green",submission_type:"PFI Self-Assessment"}); setScores(Object.fromEntries(CRITERIA.map(c=>[c.key,70]))); }} color="#34d399" outline>
          Start New Assessment
        </Btn>
      </div>
    );
  }
  return null;
}

// ── Journey B: DBN Officer Review Queue ────────────────────────────────────────
function ReviewQueue({ assessments, onRefresh }) {
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState("");
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleAction = async () => {
    if (!selected || !action) return;
    setActing(true);
    try {
      await dbnApi.assessmentAction(selected.id, { action, notes, officer_id:"dbn_officer" });
      onRefresh();
      setSelected(null);
      setAction("");
      setNotes("");
    } catch(e) {}
    setActing(false);
  };

  const handleDownloadMemo = (id) => {
    window.open(dbnApi.assessmentMemoPdf(id), "_blank");
  };

  const statusC = (s) => STATUS_COLORS[s] || "#64748b";

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1.6fr", gap:16 }}>
      {/* Queue */}
      <div>
        <p style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:10 }}>
          Assessment Queue ({assessments.length})
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {assessments.map(a => (
            <div key={a.id}
              onClick={() => setSelected(a)}
              data-testid={`assessment-${a.id}`}
              style={{
                background: selected?.id===a.id ? "rgba(52,211,153,0.08)" : "#060f1e",
                border: `1px solid ${selected?.id===a.id?"#34d399":"#0f2033"}`,
                borderRadius:10, padding:"10px 12px", cursor:"pointer",
                transition:"all .15s"
              }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontSize:10, fontWeight:700, color:"#f0f9ff" }}>{a.id}</span>
                <Badge label={a.status} color={statusC(a.status)} />
              </div>
              <p style={{ fontSize:10, color:"#94a3b8", marginBottom:4, fontWeight:600 }}>{a.project_name}</p>
              <div style={{ display:"flex", gap:6 }}>
                <Badge label={a.eligibility_status} color={eligColor(a.eligibility_status)} />
                <span style={{ fontSize:9, color:"#475569" }}>{a.sector} · {a.state}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail */}
      {selected ? (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <p style={{ fontSize:13, fontWeight:800, color:"#f0f9ff" }}>{selected.project_name}</p>
            <Btn small color="#60a5fa" onClick={() => handleDownloadMemo(selected.id)}>
              Download Memo (PDF)
            </Btn>
          </div>

          {/* Score breakdown */}
          <div style={{ background:"#060f1e", borderRadius:10, padding:"12px 14px", marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10 }}>
              <ScoreRing score={selected.overall_score} size={60} />
              <div>
                <p style={{ fontSize:16, fontWeight:900, color:eligColor(selected.eligibility_status) }}>
                  {selected.eligibility_status}
                </p>
                <p style={{ fontSize:10, color:"#475569" }}>Overall: {selected.overall_score}/100</p>
                <p style={{ fontSize:9, color:"#334155" }}>Submitted: {(selected.submitted_at||"").slice(0,10)}</p>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
              {CRITERIA.map(c => (
                <div key={c.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:9, color:"#475569" }}>{c.label}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:scoreColor(selected[c.key]||0) }}>
                    {selected[c.key]||0}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendation */}
          <div style={{ background:"#001a0a", border:"1px solid #1a4a30", borderRadius:8, padding:"10px 12px", marginBottom:12 }}>
            <p style={{ fontSize:9, color:"#475569", marginBottom:4, fontWeight:700 }}>Assessment Recommendation</p>
            <p style={{ fontSize:10, color:"#94a3b8", lineHeight:1.5 }}>{selected.recommendation}</p>
          </div>

          {/* Outstanding */}
          {selected.outstanding_actions && (
            <div style={{ marginBottom:10 }}>
              <p style={{ fontSize:9, color:"#f59e0b", fontWeight:700, marginBottom:4 }}>Outstanding Actions</p>
              <p style={{ fontSize:9, color:"#78350f", lineHeight:1.5 }}>{selected.outstanding_actions}</p>
            </div>
          )}

          {/* Officer actions */}
          {selected.status !== "Approved" && selected.status !== "Rejected" && selected.status !== "Sent to Committee" && (
            <div style={{ background:"#060f1e", borderRadius:10, padding:"12px 14px", marginTop:12 }}>
              <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff", marginBottom:10 }}>Officer Decision</p>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                {[
                  {key:"approve_dd",    label:"Approve for Due Diligence",   color:"#22c55e"},
                  {key:"approve_committee",label:"Send to Investment Committee",color:"#3b82f6"},
                  {key:"request_info", label:"Request Additional Info",      color:"#f59e0b"},
                  {key:"assign_ta",    label:"Assign Technical Assistance",  color:"#a78bfa"},
                  {key:"escalate",     label:"Escalate",                     color:"#f97316"},
                  {key:"reject",       label:"Reject",                       color:"#ef4444"},
                ].map(a => (
                  <button key={a.key} onClick={() => setAction(a.key)}
                    style={{
                      padding:"5px 10px", borderRadius:7, fontSize:9, fontWeight:700, cursor:"pointer",
                      background: action===a.key ? a.color : "transparent",
                      border:`1px solid ${a.color}`,
                      color: action===a.key ? "#fff" : a.color,
                      transition:"all .15s"
                    }}>{a.label}</button>
                ))}
              </div>
              {action && (
                <>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="Officer notes (optional)…"
                    rows={3} style={{
                      width:"100%", background:"#0a1628", border:"1px solid #1e3a5f",
                      borderRadius:8, padding:"8px 12px", fontSize:10, color:"#94a3b8",
                      resize:"none", boxSizing:"border-box", marginBottom:8
                    }} />
                  <Btn onClick={handleAction} color="#34d399" disabled={acting}>
                    {acting ? "Submitting…" : "Confirm Decision"}
                  </Btn>
                </>
              )}
            </div>
          )}

          {/* Existing decision */}
          {selected.officer_decision && (
            <div style={{ marginTop:10, background:"#0f2033", borderRadius:8, padding:"8px 12px" }}>
              <p style={{ fontSize:9, color:"#475569", marginBottom:2 }}>Officer Decision Recorded</p>
              <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff" }}>{selected.officer_decision}</p>
              {selected.officer_notes && <p style={{ fontSize:9, color:"#64748b", marginTop:3 }}>{selected.officer_notes}</p>}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", color:"#334155", fontSize:11 }}>
          Select an assessment from the queue to review
        </div>
      )}
    </div>
  );
}

// ── Workflow Visualizer ────────────────────────────────────────────────────────
const WORKFLOW_STEPS = [
  { step:1,  label:"Project Submission",          desc:"PFI submits project application with basic details",                 status:"complete" },
  { step:2,  label:"Document Upload",             desc:"Business plan, financial model, ESIA, GHG calculations",            status:"complete" },
  { step:3,  label:"AI Document Extraction",      desc:"Auto-extract: project cost, technology, sector, location, climate", status:"complete" },
  { step:4,  label:"Climate Eligibility",         desc:"Activity classification against green taxonomy",                    status:"complete" },
  { step:5,  label:"Financial Eligibility",       desc:"Creditworthiness, DSCR, IRR, debt service capacity",               status:"complete" },
  { step:6,  label:"Digital Eligibility",         desc:"Digital component assessment, platform scoring",                   status:"active"  },
  { step:7,  label:"Gender & Inclusion",          desc:"Women ownership, youth employment, rural/conflict-affected",        status:"pending" },
  { step:8,  label:"Environmental Safeguards",    desc:"IFC Performance Standards, ESIA review, community engagement",     status:"pending" },
  { step:9,  label:"Additionality Assessment",    desc:"Financial, project and development additionality scoring",          status:"pending" },
  { step:10, label:"TA Gap Identification",       desc:"Identify capability gaps requiring technical assistance",           status:"pending" },
  { step:11, label:"Investment Readiness",        desc:"Management team, regulatory status, development stage",            status:"pending" },
  { step:12, label:"IC Recommendation",           desc:"Investment Committee recommendation generated",                    status:"pending" },
  { step:13, label:"Memo Generation",             desc:"Investment Assessment Memorandum produced",                        status:"pending" },
  { step:14, label:"Committee Decision",          desc:"Approve / Conditional / Reject / Refer for TA",                   status:"pending" },
  { step:15, label:"Pipeline Entry",              desc:"Approved projects enter the formal DBN financing pipeline",        status:"pending" },
];

const STEP_COLORS = { complete:"#22c55e", active:"#f59e0b", pending:"#334155" };
const STEP_TEXT   = { complete:"#f0f9ff", active:"#f0f9ff", pending:"#475569" };

function WorkflowVisualizer() {
  const [activeStep, setActiveStep] = useState(6);
  return (
    <div>
      <p style={{ fontSize:10, color:"#475569", marginBottom:14, lineHeight:1.6 }}>
        Full end-to-end assessment workflow from project submission to pipeline entry.
        Green = completed · Amber = in progress · Grey = pending.
        Click any step to view details.
      </p>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:16 }}>
        {WORKFLOW_STEPS.map(step => (
          <div key={step.step}
            onClick={() => setActiveStep(step.step)}
            style={{
              display:"flex", alignItems:"center", gap:6, cursor:"pointer",
              background: step.step === activeStep ? "rgba(52,211,153,0.1)" : "#060f1e",
              border:`1px solid ${step.step===activeStep?"#34d399":STEP_COLORS[step.status]}40`,
              borderRadius:8, padding:"6px 10px", transition:"all .15s",
            }}>
            <div style={{ width:18, height:18, borderRadius:"50%", flexShrink:0, display:"flex",
              alignItems:"center", justifyContent:"center",
              background:STEP_COLORS[step.status],
              color:"#07111e", fontSize:9, fontWeight:900 }}>
              {step.status==="complete" ? "✓" : step.step}
            </div>
            <span style={{ fontSize:9.5, fontWeight:step.status==="active"?700:400,
              color:STEP_TEXT[step.status], whiteSpace:"nowrap" }}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
      {/* Active step detail */}
      {WORKFLOW_STEPS.filter(s => s.step===activeStep).map(step => (
        <div key={step.step} style={{ background:"#060f1e", border:`1px solid ${STEP_COLORS[step.status]}40`,
          borderRadius:12, padding:"14px 18px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
            <div style={{ width:24, height:24, borderRadius:"50%", background:STEP_COLORS[step.status],
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:900, color:"#07111e" }}>
              {step.status==="complete"?"✓":step.step}
            </div>
            <p style={{ fontSize:13, fontWeight:800, color:STEP_COLORS[step.status] }}>
              Step {step.step}: {step.label}
            </p>
            <Badge label={step.status.charAt(0).toUpperCase()+step.status.slice(1)}
              color={STEP_COLORS[step.status]} />
          </div>
          <p style={{ fontSize:11, color:"#94a3b8", lineHeight:1.6 }}>{step.desc}</p>
          <div style={{ marginTop:10, display:"flex", gap:6 }}>
            <div style={{ height:4, flex:step.step, background:STEP_COLORS[step.status], borderRadius:2 }} />
            <div style={{ height:4, flex:WORKFLOW_STEPS.length-step.step, background:"#0f2033", borderRadius:2 }} />
          </div>
          <p style={{ fontSize:9, color:"#334155", marginTop:4 }}>
            Progress: {step.step} of {WORKFLOW_STEPS.length} steps
          </p>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DBNEligibilityAssessment() {
  const [tab, setTab] = useState("queue");
  const [assessments, setAssessments] = useState([]);
  const [pfis, setPfis] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([dbnApi.assessments(), dbnApi.pfis(), dbnApi.facilities()])
      .then(([ra, rp, rf]) => {
        setAssessments(ra.data?.assessments || []);
        setPfis(rp.data?.pfis || []);
        setFacilities(rf.data?.facilities || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const byStatus = assessments.reduce((acc, a) => {
    acc[a.status] = (acc[a.status]||0)+1; return acc;
  }, {});

  if (loading) return <LoadingState />;

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="Eligibility and Investment Assessment"
        subtitle="PFI self-assessment · DBN officer review · Investment memo generation">
        <Btn small color="#34d399" onClick={() => setTab("submit")}>+ New Assessment</Btn>
      </PageHeader>

      <div style={{ padding:"16px 24px", flex:1 }}>
        {/* Stats bar */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:16 }}>
          {[
            { label:"Total Submitted",  value:assessments.length,                                    color:"#60a5fa" },
            { label:"Approved",         value:byStatus["Approved"]||byStatus["Sent to Committee"]||0, color:"#22c55e" },
            { label:"Under Review",     value:(byStatus["Under Review"]||0)+(byStatus["Submitted"]||0)+(byStatus["Information Requested"]||0), color:"#f59e0b" },
            { label:"Rejected",         value:byStatus["Rejected"]||0,                               color:"#ef4444" },
            { label:"TA Assigned",      value:byStatus["TA Assigned"]||0,                            color:"#a78bfa" },
          ].map(s => (
            <div key={s.label} style={{ background:"#0a1628", border:"1px solid #0f2033",
              borderTop:`3px solid ${s.color}`, borderRadius:12, padding:"12px 14px" }}>
              <p style={{ fontSize:22, fontWeight:900, color:s.color }}>{s.value}</p>
              <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em" }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:2, marginBottom:16, background:"#0a1628",
          border:"1px solid #0f2033", borderRadius:10, padding:4, width:"fit-content" }}>
          {[
            {key:"queue",    label:"Review Queue",          icon:ClipboardCheck},
            {key:"submit",   label:"PFI Self-Assessment",   icon:Send},
            {key:"workflow", label:"End-to-End Workflow",   icon:ChevronRight},
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              style={{
                display:"flex", alignItems:"center", gap:6, padding:"7px 16px",
                borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
                background: tab===t.key ? "rgba(52,211,153,0.15)" : "transparent",
                color: tab===t.key ? "#34d399" : "#475569",
                transition:"all .15s"
              }}>
              <t.icon size={12} /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:20 }}>
          {tab === "queue" && (
            <>
              <ReviewQueue assessments={assessments} onRefresh={load} />
              {/* Investment Readiness Radar for selected assessment */}
              {assessments.length > 0 && (
                <div style={{ marginTop:20, borderTop:"1px solid #0f2033", paddingTop:16 }}>
                  <SectionHeader title="Investment Readiness Radar" sub="Composite view of all eligibility dimensions — derived from submitted assessments" />
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                    {assessments.slice(0,2).map(a => {
                      const radarData = [
                        {axis:"Financial",     value:a.financial_score||0},
                        {axis:"Green",         value:a.green_score||0},
                        {axis:"Digital",       value:a.digital_score||0},
                        {axis:"Inclusion",     value:a.inclusion_score||0},
                        {axis:"Climate Risk",  value:a.climate_risk_score||0},
                        {axis:"ESG",           value:a.esg_score||0},
                        {axis:"Safeguards",    value:a.safeguards_score||0},
                        {axis:"Additionality", value:a.additionality_score||0},
                        {axis:"Readiness",     value:a.readiness_score||0},
                      ];
                      const color = eligColor(a.eligibility_status);
                      return (
                        <div key={a.id} style={{ background:"#060f1e", borderRadius:12, padding:14 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                            <div>
                              <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff" }}>{a.project_name}</p>
                              <p style={{ fontSize:9, color:"#475569" }}>{a.id} · Score: {a.overall_score}/100</p>
                            </div>
                            <Badge label={a.eligibility_status} color={color} />
                          </div>
                          <ResponsiveContainer width="100%" height={200}>
                            <RadarChart data={radarData}>
                              <PolarGrid stroke="#0f2033" />
                              <PolarAngleAxis dataKey="axis" tick={{ fill:"#475569", fontSize:8 }} />
                              <PolarRadiusAxis domain={[0,100]} tick={{ fill:"#334155", fontSize:7 }} />
                              <Radar name={a.project_name} dataKey="value"
                                stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2} />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
          {tab === "submit" && <AssessmentWizard pfis={pfis} facilities={facilities} onSubmit={load} />}
          {tab === "workflow" && <WorkflowVisualizer />}
        </div>
      </div>
    </div>
  );
}
